import { useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../components/controls/button";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import Image from "../../components/controls/image";
import TextInput from "../../components/controls/text-input";
import {
    FeatureCard,
    FeatureCardRow,
    FeatureCardSingleColumn,
    FeatureCardTitle,
} from "../../styles/shared/card";
import { currencyFormatter } from "../../helpers/formatters";
import { Alert, AlertTitle } from "@mui/material";
import { ApplicationOrderLine } from "../../models/order/application-order-line";
import { createSplit, fetchApproval, fetchProductSuppliers } from "../../store/features/ordering/approval/approval-slice";
import { GoogleIcon, ICON_ID } from "../../components/icons/google-icon";
import { ApplicationSupplierProductHeader } from "../../models/supplier/application-supplier-product-header";
import { ButtonGroup, Footer, MainContent } from "../../styles/shared/drawer";
import { closeDrawer } from "../../store/features/drawer/drawer-slice";

interface SpltOrderDrawerProps {
    orderLineId: number;
    branchId: number;
}

export default function SplitOrderDrawer({
    orderLineId,
    branchId,
}: SpltOrderDrawerProps) {
    const [orderLine, setOrderLine] = useState<ApplicationOrderLine>();
    const [originalOrderLine, setOriginalOrderLine] = useState<ApplicationOrderLine>();
    const [suppliers, setSuppliers] = useState<ApplicationSupplierProductHeader[]>();
    const dispatch = useAppDispatch();
    const [splitInputs, setSplitInputs] = useState<Record<number, { quantity: number; notes: string }>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (orderLineId) {
            setLoading(true);
            dispatch(fetchApproval({ orderLineId })).then((response) => {
                const orderLine = response.payload as ApplicationOrderLine
                if (orderLine) {
                    setOrderLine(orderLine);
                    setOriginalOrderLine(orderLine);
                }
                setLoading(false)
            })
        }
    }, [orderLineId, dispatch])

    useEffect(() => {
        if (orderLine && branchId) {
            dispatch(fetchProductSuppliers({ branchId, productIds: [orderLine.productId] }))
                .then(response => {
                    const payload = response.payload as Record<number, ApplicationSupplierProductHeader[]> | ApplicationSupplierProductHeader[] | null;
                    const suppliers = Array.isArray(payload)
                        ? payload
                        : payload?.[orderLine.productId] ?? [];
                    setSuppliers(
                        suppliers.filter(
                            (s) =>
                                s.supplierId !== orderLine.supplierId &&
                                !orderLine.children.some(split => split.supplierId === s.supplierId)
                        )
                    );
                })
        }
    }, [orderLine, branchId, dispatch])


    const handleCreate = async () => {
        if (orderLine) {
            await dispatch(createSplit({ orderId: orderLine.orderId, orderLineId: orderLine.id, split: orderLine.children }))
            dispatch(closeDrawer());
        }
    }

    const handleAddSplit = (supplierId: number) => {
        if (!orderLine || !suppliers) return;
        const input = splitInputs[supplierId] ?? { quantity: 0, notes: "" };
        if (!input.quantity || input.quantity <= 0) return;
        if (input.quantity >= orderLine.quantity) return;

        const selectedSupplier = suppliers.find(s => s.supplierId === supplierId);
        if (!selectedSupplier) return;

        if (orderLine.children.some(s => s.supplierId === supplierId)) return;

        const newSplit = {
            id: orderLineId,
            orderLineId: orderLineId,
            supplierId: selectedSupplier.supplierId,
            supplierName: selectedSupplier.supplierName,
            quantity: input.quantity,
            costPrice: selectedSupplier.price!,
            size: selectedSupplier.size,
            sizeUnit: selectedSupplier.sizeUnit,
            notes: input.notes,
        };

        setOrderLine({
            ...orderLine,
            quantity: orderLine.quantity - input.quantity,
            children: [...(orderLine.children ?? []), newSplit],
        });

        setSplitInputs((prev) => ({
            ...prev,
            [supplierId]: { quantity: 0, notes: "" },
        }));
    };

    const handleDeleteSplit = (supplierId: number) => {
        if (!orderLine) return;
        const deletedSplit = orderLine.children.find((s) => s.supplierId === supplierId);
        if (!deletedSplit) return;

        setOrderLine({
            ...orderLine,
            quantity: orderLine.quantity + deletedSplit.quantity,
            children: orderLine.children.filter((s) => s.supplierId !== supplierId),
        });
    };

    const availableSuppliers = suppliers?.filter((supplier) =>
        !(orderLine?.children ?? []).some((split) => split.supplierId === supplier.supplierId)
    ) ?? [];

    if (loading) {
        return <>Loading...</>
    }

    if (!orderLine) {
        return <></>
    }

    return (
        <MainContent>
            <ProductImage>
                <Image image={orderLine.productImage}></Image>
            </ProductImage>
            <ProductDescription>{orderLine.productDescription}</ProductDescription>
            <ProductDetails>
                <ProductDetailLarge>
                    <span><strong>{orderLine.supplierName} </strong> Qty:</span>
                    <ProductDetailValue>{orderLine.quantity}</ProductDetailValue>
                </ProductDetailLarge>
                <ProductDetailLarge>
                    <span>Price </span>
                    <ProductDetailValue>{currencyFormatter.format(orderLine.costPrice)}</ProductDetailValue>
                </ProductDetailLarge>
                <ProductDetailLarge>
                    <span>Cost per {orderLine.sizeUnit}</span>
                    <ProductDetailValue>{currencyFormatter.format(orderLine.costPrice / orderLine.size)}</ProductDetailValue>
                </ProductDetailLarge>
            </ProductDetails>
            {orderLine?.children?.map(split => (
                <ProductDetails>
                    <ProductLeft>
                        <GoogleIcon id={ICON_ID.Split} />
                    </ProductLeft>
                    <ProductDetailLarge>
                        <span><strong>{split.supplierName} </strong> Qty:</span>
                        <ProductDetailValue>{split.quantity}</ProductDetailValue>
                    </ProductDetailLarge>
                    <ProductDetailLarge>
                        <span>Price </span>
                        <ProductDetailValue>{currencyFormatter.format(split.costPrice)}</ProductDetailValue>
                    </ProductDetailLarge>
                    <ProductDetailLarge>
                        <span>Cost per {split.sizeUnit}</span>
                        <ProductDetailValue>{currencyFormatter.format(split.costPrice / split.size)}</ProductDetailValue>
                    </ProductDetailLarge>
                    <ProductRight>
                        <Button id={`deletesplit_${split.supplierId}`} label="" icon={ICON_ID.Close} variant="text" onClick={() => handleDeleteSplit(split.supplierId)}></Button>
                    </ProductRight>
                </ProductDetails>
            ))}
            {availableSuppliers.length > 0 ? (
                <FeatureCard>
                    <FeatureCardTitle>Choose supplier and split quantity</FeatureCardTitle>
                    {availableSuppliers.map((supplier) => {
                        const input = splitInputs[supplier.supplierId] ?? { quantity: 0, notes: "" };
                        const quantityError = input.quantity > 0 && input.quantity >= orderLine.quantity
                            ? `Quantity must be lower than ${orderLine.quantity}`
                            : undefined;

                        return (
                            <FeatureCardRow key={supplier.supplierId}>
                                <FeatureCardSingleColumn>
                                    <SupplierRow>
                                        <SupplierInfo>
                                            <div><strong>{supplier.supplierName}</strong></div>
                                            <div>{currencyFormatter.format(supplier.price ?? 0)} ({currencyFormatter.format((supplier.price ?? 0) / supplier.size)} per {supplier.sizeUnit})</div>
                                        </SupplierInfo>
                                        <SupplierControls>
                                            <TextInput
                                                id={`splitQuantity_${supplier.supplierId}`}
                                                label="Qty"
                                                type="number"
                                                value={input.quantity ? input.quantity.toString() : ""}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    if (!isNaN(value)) {
                                                        setSplitInputs((prev) => ({
                                                            ...prev,
                                                            [supplier.supplierId]: {
                                                                quantity: value,
                                                                notes: prev[supplier.supplierId]?.notes ?? ""
                                                            }
                                                        }));
                                                    }
                                                }}
                                                error={quantityError}
                                            />
                                            <TextInput
                                                id={`splitNotes_${supplier.supplierId}`}
                                                label="Notes"
                                                type="text"
                                                value={input.notes}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setSplitInputs((prev) => ({
                                                        ...prev,
                                                        [supplier.supplierId]: {
                                                            quantity: input.quantity,
                                                            notes: value
                                                        }
                                                    }));
                                                }}
                                            />
                                            <Button
                                                id={`btnSplit_${supplier.supplierId}`}
                                                label="Split"
                                                variant="outlined"
                                                icon={ICON_ID.Split}
                                                isSecondary={true}
                                                fullWidth={false}
                                                disabled={!input.quantity || input.quantity <= 0 || Boolean(quantityError)}
                                                onClick={() => handleAddSplit(supplier.supplierId)}
                                            />
                                        </SupplierControls>
                                    </SupplierRow>
                                </FeatureCardSingleColumn>
                            </FeatureCardRow>
                        );
                    })}
                </FeatureCard>
            ) : (
                <Alert severity="warning">
                    <AlertTitle>No other suppliers stock this product</AlertTitle>
                </Alert>
            )}

            <Footer>
                <ButtonGroup>
                    <Button
                        label="Cancel"
                        variant="outlined"
                        id="btnCancelDrawer"
                        onClick={() => dispatch(closeDrawer())}
                    />
                    <Button
                        id="btnCreate"
                        label="Update"
                        disabled={JSON.stringify(orderLine.children) === JSON.stringify(originalOrderLine?.children)}
                        variant="contained"
                        onClick={handleCreate}
                    />
                </ButtonGroup>
            </Footer>
        </MainContent>
    );
}



const ProductDescription = styled.div`
  text-align: center;
  padding: 10px;
  border-bottom: 3px solid ${(props) => props.theme.application.scheme.primary};
`;

const ProductDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 4px;
  column-gap: 20px;
  row-gap: 5px;
  border: 1px dashed ${(props) => props.theme.application.border};
  padding: 10px;
  border-radius: 3px;
  justify-content: center;
  position:relative;    
  width:95%

    
`;

const ProductImage = styled.div`
  img {
    width: 100px;
    height: 100px;
  }
`;


const ProductDetailLarge = styled.div`
font-size:1.2crem;
  max-width:70%;
`;
const ProductDetailValue = styled.span`
  margin-left: 5px;
  font-weight: 600;
`;
const SupplierRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1.5fr 2.5fr;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SupplierInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SupplierControls = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const ProductRight = styled.div`
position:absolute;
right:0;
width:40px;
top:10%;

`
const ProductLeft = styled.div`
position:absolute;
left:0
` 